const PRODUCT_PROMO_GALLERY = 'product-promo-gallery';
const DESKTOP_BREAKPOINT = '(min-width: 750px)';

class ProductPromoGallery extends HTMLElement {
  #viewport;
  #track;
  #previousButton;
  #nextButton;
  #arrows;
  #resizeObserver;
  #mediaQuery;
  #animationFrame;

  connectedCallback() {
    const hasCarousel = this.dataset.layoutMobile === 'carousel' || this.dataset.layoutDesktop === 'carousel';
    if (!hasCarousel) return;

    this.#viewport = this.querySelector('[data-gallery-viewport]');
    this.#track = this.querySelector('.koh-promo-gallery__track');
    this.#previousButton = this.querySelector('[data-gallery-previous]');
    this.#nextButton = this.querySelector('[data-gallery-next]');
    this.#arrows = this.querySelector('[data-gallery-arrows]');

    if (!(this.#viewport instanceof HTMLElement) || !(this.#track instanceof HTMLElement)) return;

    this.#mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);
    this.#previousButton?.addEventListener('click', this.#goPrevious);
    this.#nextButton?.addEventListener('click', this.#goNext);
    this.#viewport.addEventListener('scroll', this.#queueUpdate, { passive: true });
    this.#viewport.addEventListener('keydown', this.#handleKeydown);

    if (typeof this.#mediaQuery.addEventListener === 'function') {
      this.#mediaQuery.addEventListener('change', this.#queueUpdate);
    } else {
      this.#mediaQuery.addListener(this.#queueUpdate);
    }

    this.#resizeObserver = new ResizeObserver(this.#queueUpdate);
    this.#resizeObserver.observe(this.#viewport);

    requestAnimationFrame(this.#update);
  }

  disconnectedCallback() {
    this.#previousButton?.removeEventListener('click', this.#goPrevious);
    this.#nextButton?.removeEventListener('click', this.#goNext);
    this.#viewport?.removeEventListener('scroll', this.#queueUpdate);
    this.#viewport?.removeEventListener('keydown', this.#handleKeydown);

    if (this.#mediaQuery) {
      if (typeof this.#mediaQuery.removeEventListener === 'function') {
        this.#mediaQuery.removeEventListener('change', this.#queueUpdate);
      } else {
        this.#mediaQuery.removeListener(this.#queueUpdate);
      }
    }

    this.#resizeObserver?.disconnect();

    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
  }

  #goPrevious = () => this.#move(-1);

  #goNext = () => this.#move(1);

  #handleKeydown = (event) => {
    if (!this.#isCarouselActive()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#move(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#move(1);
    }
  };

  #isCarouselActive() {
    const isDesktop = this.#mediaQuery?.matches ?? window.matchMedia(DESKTOP_BREAKPOINT).matches;
    return isDesktop ? this.dataset.layoutDesktop === 'carousel' : this.dataset.layoutMobile === 'carousel';
  }

  #move(direction) {
    if (!(this.#viewport instanceof HTMLElement) || !(this.#track instanceof HTMLElement)) return;
    if (!this.#isCarouselActive()) return;

    const firstItem = this.querySelector('.koh-promo-gallery__item');
    if (!(firstItem instanceof HTMLElement)) return;

    const gap = Number.parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const distance = firstItem.getBoundingClientRect().width + gap;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#viewport.scrollBy({
      left: direction * distance,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  #syncPeekBasis() {
    if (!(this.#viewport instanceof HTMLElement) || !(this.#track instanceof HTMLElement)) return;

    const isMobile = !(this.#mediaQuery?.matches ?? window.matchMedia(DESKTOP_BREAKPOINT).matches);
    const shouldMeasurePeek =
      isMobile && this.dataset.layoutMobile === 'carousel' && this.dataset.mobileColumns === 'peek';

    if (!shouldMeasurePeek) {
      this.style.removeProperty('--koh-gallery-mobile-basis-measured');
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const itemWidth = Math.max(1, (this.#viewport.clientWidth - gap) * 0.75);
    this.style.setProperty('--koh-gallery-mobile-basis-measured', `${itemWidth}px`);
  }

  #queueUpdate = () => {
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#syncPeekBasis();
    this.#updateControls();
  };

  #updateControls() {
    if (!(this.#viewport instanceof HTMLElement)) return;

    if (!this.#isCarouselActive()) {
      if (this.#arrows instanceof HTMLElement) this.#arrows.hidden = true;
      if (this.#previousButton instanceof HTMLButtonElement) this.#previousButton.disabled = true;
      if (this.#nextButton instanceof HTMLButtonElement) this.#nextButton.disabled = true;
      return;
    }

    const maximumScroll = Math.max(0, this.#viewport.scrollWidth - this.#viewport.clientWidth);
    const currentScroll = Math.max(0, this.#viewport.scrollLeft);
    const hasOverflow = maximumScroll > 2;

    if (this.#arrows instanceof HTMLElement) this.#arrows.hidden = !hasOverflow;
    if (this.#previousButton instanceof HTMLButtonElement) this.#previousButton.disabled = currentScroll <= 2;
    if (this.#nextButton instanceof HTMLButtonElement) {
      this.#nextButton.disabled = currentScroll >= maximumScroll - 2;
    }
  }
}

if (!customElements.get(PRODUCT_PROMO_GALLERY)) {
  customElements.define(PRODUCT_PROMO_GALLERY, ProductPromoGallery);
}
